import { getItemTooltip, getItemColor } from '@helpers/items';
import { Tooltip } from '@components/ui/Tooltip';
import { IconClock } from '@tabler/icons-react';
import { useCollectQuestMutation } from '@http';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';

export function Quests() {
  const [_update, setUpdate] = useState(0);

  const { t } = useTranslation(undefined, { keyPrefix: 'Hub.Quests' });
  const { t: tCommon } = useTranslation();
  const { mutateAsync } = useCollectQuestMutation();
  const { userData, refetch, setIsFetching } = useAppStore();

  useEffect(() => {
    const interval = setInterval(() => {
      setUpdate((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const quests = userData.quests.sort((a) => {
    if (a.type === 'DAILY_LOGIN') return -1;
    return 0;
  }).filter(q => !q.collected);

  const refreshDate = new Date(quests[0]?.createdAt ?? 0);
  const timeLeft = new Date(refreshDate.getTime() - Date.now()).toISOString().slice(11, 19);

  const generateName = (quest: typeof quests[0]) => {
    if (quest.type === 'COLLECT') return tCommon(`Items.${quest.key}`);
    const [faction, type] = quest.key.split('_');
    return tCommon(`Factions.${faction}.${type}`);
  };

  const generateReward = (quest: typeof quests[0]) => {
    return tCommon(`Items.${quest.rewardType}`);
  };

  const getColor = (quest: typeof quests[0]) => {
    if (quest.type === 'KILL') return 'text-red-500';
    const data = Shared.itemData.find((item) => item.key === quest.key);
    if (!data) return '';
    return getItemColor(data);
  };

  const collectQuest = async (id: string) => {
    setIsFetching(true);

    await mutateAsync({ id }, {
      onSuccess: () => refetch(),
    });

    toast(t('questCollected'), { type: 'success' });
  };

  return (
    <div className="tutorial-quest">
      <div className="flex items-center justify-between mt-4 px-2 mb-1">
        <p className="text-lg">{t('quests')}</p>
        <div className="flex items-center text-xs text-gray-400 text-mono">
          <IconClock className="mr-1" size={12} />
          <p>{timeLeft}</p>
        </div>
      </div>

      {quests.length === 0 && (
        <div className="quest-container flex items-center justify-center h-36">
          <p className="text-gray-500 text-sm">{t('completed')}</p>
        </div>
      )}

      {quests.map((quest) => (
        <div className={`${quest.type} quest-container`} key={quest.id}>
          {quest.type !== 'DAILY_LOGIN' && (
            <div className="quest-img-container grid-overlay">
              {quest.type === 'COLLECT' && (
                <Tooltip overlay={getItemTooltip(quest.key)} targetClassName="p-1">
                  <img src={`/assets/items/${quest.key}.avif`} alt={quest.key} />
                </Tooltip>
              )}
              {quest.type === 'KILL' && (
                <img src={`/img/factions/${quest.key.toLowerCase()}_transparent.avif`} alt={quest.key} />
              )}
            </div>
          )}

          <div className="ml-2 flex flex-col justify-center text-sm">
            <p className={`${getColor(quest)}`}>
              {t(`Types.${quest.type}`)}{' '}
              {quest.type !== 'DAILY_LOGIN' && generateName(quest)}
            </p>
            <p className="text-primary text-xs">
              {t('reward', { amount: quest.rewardAmount })}{' '}
              {generateReward(quest)}
            </p>
          </div>

          <Button
            className="btn-active border-0 w-18 ml-auto border-l"
            disabled={quest.progress < quest.amount}
            onClick={() => collectQuest(quest.id)}
          >
            {Math.min(quest.amount, quest.progress)} / {quest.amount}
          </Button>
        </div>
      ))}
      <div className="w-full border-t border-secondary" />
    </div>
  );
}